import React, { useEffect, useState } from "react";
import LineSelect from "./LineSelect";
import "./PhoneField.css";

const CODES_URL = "https://countriesnow.space/api/v0.1/countries/codes";

function readableName(name) {
  return String(name || "").replace(/([a-z])([A-Z])/g, "$1 $2").trim();
}

const MIN_NUMBER_LENGTH = 7;
const MAX_NUMBER_LENGTH = 12;

function combinedPhone(dialCode, number) {
  const code = String(dialCode || "").trim();
  const digits = String(number || "").replace(/\D/g, "");
  if (!code || !digits) return "";
  return `${code} ${digits}`;
}

function readNumber(value) {
  const digitsOnly = String(value || "").replace(/\D/g, "");
  const removedLeadingZero = digitsOnly.startsWith("0");
  const withoutZero = digitsOnly.replace(/^0+/, "");
  const tooLong = withoutZero.length > MAX_NUMBER_LENGTH;
  const digits = withoutZero.slice(0, MAX_NUMBER_LENGTH);
  const longEnough = digits.length >= MIN_NUMBER_LENGTH && digits.length <= MAX_NUMBER_LENGTH;
  let message = "";
  if (removedLeadingZero && !longEnough) {
    message = "Leave out the 0. The country code is already selected.";
  } else if (tooLong) {
    message = "This number is too long.";
  }
  return { digits, message, complete: longEnough && !message };
}

async function loadDialCodes() {
  const response = await fetch(CODES_URL);
  if (!response.ok) throw new Error("Country codes could not be loaded.");
  const payload = await response.json();
  const rows = Array.isArray(payload?.data) ? payload.data : [];
  return rows
    .map((row) => ({
      code: String(row?.code || "").trim(),
      name: readableName(row?.name),
      dialCode: String(row?.dial_code || "").trim(),
    }))
    .filter((row) => row.code && row.name && row.dialCode.startsWith("+"))
    .sort((left, right) => left.name.localeCompare(right.name));
}

export default function PhoneField({ onChange, onReady }) {
  const [codes, setCodes] = useState([]);
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const [countryCode, setCountryCode] = useState("");
  const [number, setNumber] = useState("");
  const [notice, setNotice] = useState("");
  const [showLength, setShowLength] = useState(false);

  useEffect(() => {
    let active = true;
    setStatus("loading");
    loadDialCodes()
      .then((rows) => {
        if (!active) return;
        setCodes(rows);
        setCountryCode((current) => {
          if (current) return current;
          const southAfrica = rows.find((row) => row.code === "ZA")
            || rows.find((row) => row.dialCode === "+27" && /south africa/i.test(row.name));
          return southAfrica?.code || "";
        });
        setStatus(rows.length ? "ready" : "error");
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  useEffect(() => {
    onReady?.(status);
  }, [onReady, status]);

  const selected = codes.find((row) => row.code === countryCode);
  const phone = combinedPhone(selected?.dialCode, number);
  const lengthNotice = showLength && number && number.length < MIN_NUMBER_LENGTH
    ? "Enter the full number."
    : "";
  const phoneNotice = notice || lengthNotice;

  const publish = (nextCode, nextNumber, nextNotice) => {
    const match = codes.find((row) => row.code === nextCode);
    const complete = nextNumber.length >= MIN_NUMBER_LENGTH && nextNumber.length <= MAX_NUMBER_LENGTH;
    onChange(combinedPhone(match?.dialCode, nextNumber), {
      valid: Boolean(match?.dialCode) && complete && !nextNotice,
      message: nextNotice,
    });
  };

  return (
    <div className="phone-field" data-phone={phone}>
      <div className="phone-field__code">
        <LineSelect
          value={countryCode}
          placeholder={status === "loading" ? "Loading…" : "Code"}
          disabled={status !== "ready"}
          options={codes.map((row) => ({
            value: row.code,
            label: `${row.dialCode} ${row.name}`,
          }))}
          onChange={(next) => {
            setCountryCode(next);
            publish(next, number, notice || lengthNotice);
          }}
        />
      </div>
      <input
        className="phone-field__number"
        value={number}
        inputMode="tel"
        autoComplete="tel-national"
        placeholder="Number"
        aria-label="Phone number"
        aria-invalid={phoneNotice ? "true" : "false"}
        onBlur={() => {
          setShowLength(true);
          if (number && number.length < MIN_NUMBER_LENGTH) {
            publish(countryCode, number, notice || "Enter the full number.");
          }
        }}
        onChange={(event) => {
          const next = readNumber(event.target.value);
          const nextNotice = next.message;
          setNumber(next.digits);
          setNotice(nextNotice);
          if (next.complete) setShowLength(false);
          publish(countryCode, next.digits, nextNotice);
        }}
      />
      {phoneNotice ? <p className="phone-field__notice">{phoneNotice}</p> : null}
      {status === "error" ? (
        <p className="phone-field__status">
          Country codes could not be loaded.
          <button type="button" className="phone-field__retry" onClick={() => setAttempt((current) => current + 1)}>
            Try again
          </button>
        </p>
      ) : null}
    </div>
  );
}
